import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { from, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppService } from '../../app.service';
import { Datawarehouse } from '../../app.model';

/**
 * Resolves `/u/warehouse/type/:type` (for example `BIGQUERY`) to `/u/warehouse/<datawarehouseId>`.
 *
 * Warehouse ids are data, not constants: they differ between environments, so a caller that
 * only knows the kind of warehouse (iQ's setup wizard) links by type and Daton looks the id up
 * here. The query string, including the `iq_setup` context, carries over. If the type is not
 * found or the list cannot be loaded, the user lands on the destinations listing instead.
 */
@Injectable({
  providedIn: 'root'
})
export class WarehouseTypeGuard {
  constructor(private appService: AppService, private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<UrlTree> {
    const baseWord = this.appService.getBaseWord();
    const type = (route.paramMap.get('type') || '').toUpperCase();
    const queryParams = route.queryParams;

    const listing = (): UrlTree =>
      this.router.createUrlTree([`/${baseWord}/connectors/datawarehouses-listing`], { queryParams });

    return from(this.appService.getDatawarehouses()).pipe(
      map((warehouses: Datawarehouse[]) => {
        const match = (warehouses || []).find(
          (warehouse) => (warehouse.type || '').toUpperCase() === type && warehouse.datawarehouseId != null
        );
        return match
          ? this.router.createUrlTree([`/${baseWord}/warehouse/${match.datawarehouseId}`], { queryParams })
          : listing();
      }),
      catchError(() => of(listing()))
    );
  }
}
